import React from "react";
import { Link } from "react-router-dom";
import { PageSEO } from "@/components/PageSEO";
import {
  BarChart3, Target, PieChart, Wallet, BadgePercent, Compass,
  ArrowRight, CheckCircle, HelpCircle, Quote
} from "lucide-react";

/*
 * Pagina servizio: /controllo-di-gestione-micro-piccole-imprese
 * Testi definitivi approvati dalla committente: NON modificare, tagliare o riscrivere.
 * Prerenderizzata a ogni build (vedi scripts/seo-routes.js) e presente in public/sitemap.xml.
 */

const P = ({ children, className = "" }) => (
  <p className={`text-[#3B4658] leading-relaxed mb-4 ${className}`}>{children}</p>
);

const Pull = ({ children }) => (
  <p className="font-heading text-2xl md:text-3xl text-[#0D1527] leading-snug border-l-4 border-gold pl-6 my-8">
    {children}
  </p>
);

const H2 = ({ children, icon: Icon }) => (
  <div className="flex items-start gap-4 mb-6">
    {Icon && <Icon className="text-gold flex-shrink-0 mt-1.5" size={28} />}
    <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] leading-tight">{children}</h2>
  </div>
);

const H3 = ({ children }) => (
  <h3 className="font-heading text-xl md:text-2xl text-[#0D1527] mb-3 leading-snug">{children}</h3>
);

const Elenco = ({ voci, testid }) => (
  <ul className="space-y-3 my-6" data-testid={testid}>
    {voci.map((v) => (
      <li key={v} className="flex items-start gap-3">
        <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
        <span className="text-[#3B4658] leading-relaxed">{v}</span>
      </li>
    ))}
  </ul>
);

const NUMERI = [
  {
    icon: BarChart3,
    h3: "Quanto stai guadagnando davvero?",
    p: [
      "Il fatturato è importante. Ma fatturare tanto non significa necessariamente guadagnare tanto.",
      "Puoi aumentare le vendite del 20% e ritrovarti con un utile più basso, perché nel frattempo sono aumentati i costi.",
      "La domanda non è soltanto quanto vendi. È quanto ti rimane dopo aver sostenuto i costi della tua attività.",
      "Analizzando i risultati economici periodici possiamo capire se la tua impresa sta producendo un utile e come sta cambiando nel tempo."
    ]
  },
  {
    icon: PieChart,
    h3: "Dove guadagni e dove perdi margine?",
    p: [
      "Non tutti i prodotti, i servizi o i clienti contribuiscono allo stesso modo ai risultati della tua azienda.",
      "Alcuni possono generare molto fatturato, ma lasciare margini ridotti. Altri, magari meno appariscenti, possono essere molto più redditizi.",
      "Con l'analisi dei margini, quando i dati disponibili lo consentono, possiamo individuare quali attività contribuiscono maggiormente alla copertura dei costi e al risultato economico.",
      "Perché vendere di più ciò che rende poco non è sempre la strada giusta per guadagnare di più."
    ]
  },
  {
    icon: Target,
    h3: "Quanto ti costa tenere in piedi l'azienda?",
    p: [
      "Affitto, personale, utenze, fornitori, servizi, finanziamenti.",
      "I costi non sono tutti uguali e non si comportano tutti allo stesso modo.",
      "Alcuni aumentano quando vendi di più. Altri rimangono anche quando il fatturato diminuisce.",
      "Conoscere questa differenza aiuta a capire quanto devi vendere per coprire i costi e raggiungere il punto di pareggio.",
      "E soprattutto quanto spazio hai per affrontare un periodo difficile senza compromettere l'equilibrio dell'azienda."
    ]
  },
  {
    icon: Wallet,
    h3: "Avrai abbastanza liquidità per i prossimi pagamenti?",
    p: [
      "Un'azienda può essere redditizia e avere comunque problemi di liquidità.",
      "Perché emettere una fattura non significa necessariamente averla già incassata. E perché fornitori, stipendi, rate e imposte hanno scadenze che non aspettano.",
      "Analizzando incassi, pagamenti e impegni futuri possiamo costruire previsioni di liquidità e individuare in anticipo eventuali momenti di tensione finanziaria.",
      "Non basta sapere se stai guadagnando. Devi anche sapere se avrai i soldi disponibili quando ti serviranno."
    ]
  },
  {
    icon: BadgePercent,
    h3: "Quante imposte stai maturando?",
    p: [
      "Le imposte non dovrebbero essere una sorpresa da scoprire soltanto quando arriva il momento di pagarle.",
      "Con una contabilità aggiornata e una pianificazione fiscale periodica possiamo elaborare stime delle imposte in formazione, aggiornandole in base all'andamento dell'attività.",
      "Non si tratta di conoscere oggi con certezza quanto pagherai tra diversi mesi.",
      "Si tratta di avere una previsione ragionevole, per organizzare meglio la liquidità e valutare tempestivamente le scelte fiscali consentite.",
      "Perché conoscere in anticipo un impegno fiscale ti permette di prepararti, anziché subirlo."
    ]
  },
  {
    icon: Compass,
    h3: "Stai andando nella direzione giusta?",
    p: [
      "Un numero, da solo, racconta poco.",
      "Acquista significato quando lo confronti con il passato, con un obiettivo o con quello che avevi previsto.",
      "Per questo analizziamo gli scostamenti tra risultati effettivi e attesi, individuiamo le anomalie e valutiamo quali interventi possono essere utili.",
      "Non per giudicare ciò che hai fatto.",
      "Ma per aiutarti a decidere cosa fare da domani."
    ]
  }
];

const PASSI_LAVORO = [
  {
    h3: "Partiamo da una contabilità aggiornata",
    p: [
      "Per prendere decisioni attendibili servono dati affidabili.",
      "Per questo partiamo dalla contabilità della tua impresa, aggiornata con regolarità, e dalle informazioni necessarie a comprendere come sta andando realmente l'attività.",
      "Non dobbiamo aspettare la chiusura dell'anno per iniziare a ragionare sui risultati.",
      "Il bilancio non deve essere soltanto un documento da depositare. Deve diventare uno strumento da utilizzare durante l'anno."
    ]
  },
  {
    h3: "Mettiamo in chiaro i numeri che contano",
    p: [
      "Non tutte le imprese hanno gli stessi problemi e non tutti gli indicatori sono utili allo stesso modo.",
      "Un ristorante deve conoscere l'incidenza delle materie prime e del personale. Un'attività commerciale deve capire quali prodotti generano margine. Un'impresa di servizi deve valutare la redditività delle proprie prestazioni e l'utilizzo delle risorse.",
      "Individuiamo quindi le informazioni più importanti per la tua attività e le organizziamo in report comprensibili.",
      "Non ti sommergo di numeri. Ti aiuto a concentrarti su quelli che possono cambiare le tue decisioni."
    ]
  },
  {
    h3: "Guardiamo avanti, non soltanto indietro",
    p: [
      "Sapere come è andato il mese scorso è utile.",
      "Ma sapere come potrebbe andare il prossimo trimestre può essere ancora più importante.",
      "Per questo affianchiamo ai risultati già conseguiti le previsioni economiche, finanziarie e fiscali, aggiornandole quando cambiano le condizioni dell'attività.",
      "Possiamo così valutare in anticipo l'impatto di un investimento, di un aumento dei costi, di una nuova assunzione o di una variazione delle vendite.",
      "Non possiamo prevedere il futuro con certezza. Possiamo però prepararci a scenari diversi, anziché affrontarli all'ultimo momento."
    ]
  },
  {
    h3: "Ci confrontiamo ogni mese",
    p: [
      "I report non devono finire in una cartella del computer.",
      "Durante i nostri incontri periodici analizziamo insieme l'andamento dell'azienda, individuiamo gli scostamenti e ragioniamo sulle possibili azioni correttive.",
      "Ti spiego i numeri senza tecnicismi inutili, perché tu possa comprenderli e utilizzarli.",
      "L'obiettivo non è dirti semplicemente che un costo è aumentato.",
      "È capire perché è aumentato, quali conseguenze può avere e quali alternative hai a disposizione."
    ]
  },
  {
    h3: "Trasformiamo l'analisi in decisioni",
    p: [
      "Alla fine, ciò che conta non è quanto è bello un report.",
      "È quello che riesci a fare grazie alle informazioni che contiene.",
      "Potresti scoprire che devi rivedere alcuni prezzi, ridurre un costo, modificare il mix delle vendite, rimandare un investimento oppure cogliere un'opportunità di crescita.",
      "Le decisioni restano tue, come è giusto che sia.",
      "Il mio compito è metterti nelle condizioni di prenderle con maggiore consapevolezza, utilizzando numeri aggiornati, analisi e previsioni concrete."
    ]
  }
];

const PASSI_AVVIO = [
  {
    h3: "Partiamo dalla tua situazione attuale",
    p: [
      "Prima di parlare di report, indicatori o previsioni, voglio capire come funziona la tua azienda.",
      "Quali sono le tue difficoltà? Quali decisioni devi prendere? Che cosa vorresti migliorare?",
      "Analizziamo le informazioni disponibili e valutiamo quali aspetti della gestione meritano maggiore attenzione.",
      "Perché un ristorante, un negozio e un'impresa di servizi non hanno necessariamente bisogno degli stessi strumenti."
    ]
  },
  {
    h3: "Organizziamo i dati e definiamo cosa controllare",
    p: [
      "Individuiamo le informazioni necessarie e impostiamo un sistema di analisi adatto alle dimensioni e alle caratteristiche della tua impresa.",
      "L'obiettivo non è complicare la gestione amministrativa.",
      "È costruire un metodo che ti permetta di avere periodicamente un quadro chiaro dell'andamento aziendale.",
      "Pochi numeri, quelli giusti, letti nel modo corretto."
    ]
  },
  {
    h3: "Ogni mese facciamo il punto della situazione",
    p: [
      "Una volta avviato il percorso, analizziamo i risultati aggiornati e ci confrontiamo su ciò che sta accadendo.",
      "Vediamo quali obiettivi stai raggiungendo, quali costi o margini richiedono attenzione e come stanno evolvendo le previsioni economiche, finanziarie e fiscali.",
      "Non devi interpretare da solo decine di tabelle.",
      "Ti spiego cosa significano i numeri e ragioniamo insieme sulle decisioni da prendere."
    ]
  },
  {
    h3: "Il controllo diventa parte del tuo modo di gestire l'azienda",
    p: [
      "Con il tempo, non dovrai più aspettare il bilancio annuale per scoprire come sta andando la tua attività.",
      "Avrai un confronto periodico e informazioni più tempestive per valutare investimenti, costi, prezzi e opportunità.",
      "Non significa eliminare gli imprevisti o avere la certezza di ogni risultato.",
      "Significa affrontare le decisioni con maggiore consapevolezza e meno improvvisazione."
    ]
  }
];

const ADATTO_A = [
  ["La tua azienda lavora, ma non sai quanto guadagna davvero.", "Il fatturato lo conosci. L'utile aggiornato, molto meno."],
  ["Vuoi crescere, ma hai paura di fare il passo più lungo della gamba.", "Vorresti assumere, investire o ampliare l'attività, ma non sai se puoi permettertelo."],
  ["Hai la sensazione che i costi ti stiano sfuggendo di mano.", "Vedi aumentare le spese, ma non riesci a capire quali incidano maggiormente sui risultati."],
  ["Non vuoi più sorprese con le imposte.", "Preferisci avere previsioni periodiche e il tempo necessario per organizzarti."],
  ["Vuoi capire quali attività ti fanno guadagnare.", "Per concentrare tempo, risorse ed energie su ciò che produce risultati."],
  ["Cerchi un commercialista con cui confrontarti sui numeri.", "Un professionista che, oltre alla gestione degli adempimenti, conosca la tua azienda e ti aiuti a interpretarne i numeri."]
];

const FAQ = [
  {
    q: "Il controllo di gestione serve anche se ho una piccola azienda?",
    a: [
      "Assolutamente sì.",
      "Non è necessario avere un'impresa con decine di dipendenti o milioni di euro di fatturato.",
      "Anche una piccola attività deve stabilire prezzi, sostenere costi, gestire la liquidità e prendere decisioni importanti.",
      "Anzi, quando le risorse sono limitate, un errore può avere conseguenze particolarmente pesanti.",
      "Il controllo di gestione viene adattato alle dimensioni dell'azienda, senza introdurre strumenti inutilmente complicati."
    ]
  },
  {
    q: "Qual è la differenza tra contabilità e controllo di gestione?",
    a: [
      "La contabilità registra e organizza i fatti economici e finanziari dell'impresa ed è fondamentale per gli adempimenti e per predisporre il bilancio.",
      "Il controllo di gestione utilizza queste informazioni, insieme ad altri dati aziendali, per analizzare i risultati, individuare eventuali problemi e supportare le decisioni.",
      "La contabilità ti dice cosa è successo. Il controllo di gestione ti aiuta a capire perché e cosa puoi fare.",
      "I due strumenti non sono alternativi: lavorano insieme."
    ]
  },
  {
    q: "Devo cambiare commercialista per attivare il controllo di gestione?",
    a: [
      "Non necessariamente.",
      "Dipende dalla situazione della tua azienda, dalla disponibilità dei dati e dal livello di collaborazione necessario.",
      "In alcuni casi è possibile affiancare il controllo di gestione alla contabilità già seguita da un altro professionista.",
      "In altri può essere più efficace integrare contabilità, analisi periodica e pianificazione fiscale in un unico percorso.",
      "Valutiamo insieme quale soluzione sia più adatta alla tua impresa."
    ]
  },
  {
    q: "Quanto tempo devo dedicare al controllo di gestione?",
    a: [
      "Non devi trascorrere ore a studiare bilanci o imparare formule.",
      "Nella fase iniziale servirà comprendere come funziona la tua attività e organizzare le informazioni necessarie.",
      "Successivamente il lavoro si basa su analisi periodiche e incontri di confronto, normalmente mensili.",
      "Il tuo contributo è importante perché conosci l'azienda, i clienti e le decisioni che devi affrontare.",
      "Il mio compito è aiutarti a leggere i numeri. Il tuo è utilizzarli per guidare l'impresa."
    ]
  },
  {
    q: "Dopo quanto tempo posso vedere i primi risultati?",
    a: [
      "Dipende dalla situazione iniziale, dalla qualità dei dati disponibili e dagli interventi necessari.",
      "Già dalle prime analisi possiamo individuare informazioni utili sull'andamento economico, sui costi e sui margini.",
      "Alcuni problemi possono emergere rapidamente; altri richiedono più tempo per essere compresi e affrontati.",
      "Il controllo di gestione non garantisce automaticamente un aumento dell'utile.",
      "Ti permette però di conoscere meglio la tua azienda, individuare tempestivamente le criticità e prendere decisioni più consapevoli."
    ]
  }
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a.join(" ") }
  }))
};

const INDICE = [
  ["cos-e", "Che cos'è il controllo di gestione (e perché non è soltanto contabilità)"],
  ["piccole-imprese", "Serve anche alle piccole imprese. Soprattutto alle piccole imprese"],
  ["numeri-ogni-mese", "I numeri che ogni imprenditore dovrebbe conoscere ogni mese"],
  ["come-lavoriamo", "Come lavoriamo insieme: dai numeri alle decisioni"],
  ["a-chi-e-adatto", "A chi è adatto il controllo di gestione?"],
  ["come-iniziamo", "Come iniziamo a lavorare insieme?"],
  ["domande-frequenti", "Le domande più frequenti sul controllo di gestione"]
];

const ControlloGestionePage = () => (
  <main className="pt-20" data-testid="controllo-gestione-page">
    <PageSEO
      title="Controllo di gestione per piccole imprese | Silvia Casali"
      description="Controllo di gestione per micro e piccole imprese: margini, utile, liquidità e previsioni fiscali. Scopri come prendere decisioni con numeri aggiornati."
      keywords="controllo di gestione micro imprese, controllo di gestione piccole imprese, controllo di gestione commercialista, margini, liquidità, imposte"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }} />

    {/* HERO */}
    <section className="py-16 md:py-24 bg-[#FAF8F5]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="text-gold font-bold tracking-widest text-sm mb-5">CONTROLLO DI GESTIONE</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] leading-tight mb-6">
          CONTROLLO DI GESTIONE PER<br /><span className="text-gold">MICRO E PICCOLE IMPRESE</span>
        </h1>
        <p className="font-heading text-2xl md:text-3xl text-[#0D1527] leading-snug mb-10" data-testid="controllo-gestione-sottotitolo">
          La tua azienda fattura. Ma sai quanto ti resta davvero?
        </p>

        <P>Ogni giorno prendi decisioni per la tua azienda.</P>
        <P>Decidi i prezzi, fai acquisti, paghi fornitori e dipendenti, investi, cerchi nuovi clienti. Lavori per far crescere il fatturato.</P>
        <P>Ma c'è una domanda alla quale non sempre riesci a rispondere:</P>
        <Pull>Di tutto quello che incasso, quanto mi resta davvero?</Pull>
        <P>Magari il fatturato aumenta, ma sul conto corrente i soldi sembrano non bastare mai.</P>
        <P>Oppure lavori tantissimo, hai clienti, il telefono squilla, l'attività funziona. Eppure non sai con precisione quali prodotti o servizi ti fanno guadagnare e quali, invece, stanno consumando i tuoi margini.</P>
        <P>E quando arriva il momento di pagare le imposte, scopri quanto devi versare quando ormai hai poco tempo per organizzarti.</P>
        <P>Il problema non è necessariamente che la tua azienda stia andando male.</P>
        <P className="font-bold text-[#0D1527]">Il problema è che non hai le informazioni giuste, nel momento in cui ti servono.</P>
        <P>È qui che entra in gioco il controllo di gestione.</P>
        <P>Non per riempirti di tabelle o complicarti la vita con altri numeri.</P>
        <P>Ma per aiutarti a capire, mese dopo mese:</P>
        <Elenco
          testid="controllo-gestione-benefici"
          voci={[
            "Quanto stai realmente guadagnando.",
            "Dove si stanno riducendo i tuoi margini.",
            "Quali costi stanno pesando di più.",
            "Quanta liquidità ti servirà nei prossimi mesi.",
            "Quali imposte stai maturando.",
            "Quali decisioni puoi prendere oggi per migliorare i risultati di domani."
          ]}
        />
        <P>Perché un imprenditore non dovrebbe scoprire come è andata la propria azienda quando l'anno è già finito. Dovrebbe poterlo sapere mentre è ancora in tempo per cambiare le cose.</P>
      </div>
    </section>

    {/* INDICE */}
    <section className="py-10 bg-[#F4EFEA] border-y border-[#E8E2D5]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="font-heading text-lg text-[#0D1527] mb-4">In questa pagina</p>
        <ol className="space-y-2 text-sm" data-testid="controllo-gestione-indice">
          {INDICE.map(([id, label], i) => (
            <li key={id}>
              <a href={`#${id}`} className="text-[#4A5568] hover:text-gold transition-colors">
                <span className="text-gold font-bold mr-2">{i + 1}.</span>{label}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </section>

    {/* COS'È */}
    <section id="cos-e" className="py-16 md:py-20 bg-[#FAF8F5]" data-testid="controllo-gestione-sezione-cos-e">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <H2 icon={Compass}>Che cos'è il controllo di gestione (e perché non è soltanto contabilità)</H2>
        <P>Il bilancio ti dice come è andata la tua azienda.</P>
        <P>Ma se lo ricevi una volta all'anno, quando ormai i giochi sono fatti, a cosa ti serve per decidere cosa fare domani?</P>
        <P>Immagina di scoprire a marzo che nell'anno precedente hai perso margine su un prodotto, che il costo del personale è cresciuto troppo o che una parte delle tue vendite non era redditizia.</P>
        <P>Sono informazioni importanti. Ma arrivano tardi.</P>
        <P className="font-bold text-[#0D1527]">Il controllo di gestione serve a conoscere questi numeri mentre puoi ancora intervenire.</P>
        <P>Significa analizzare periodicamente i risultati della tua attività, confrontarli con quelli attesi e capire dove stai guadagnando, dove stai perdendo e quali azioni possono migliorare la situazione.</P>
        <P>Non sostituisce la contabilità: parte proprio da una contabilità corretta e aggiornata, e la trasforma in uno strumento per guidare l'azienda.</P>
        <P>Non devi diventare un esperto di bilanci. Non devi imparare decine di formule.</P>
        <P>Devi poter rispondere a domande semplici:</P>
        <Elenco
          testid="controllo-gestione-domande-semplici"
          voci={[
            "Sto guadagnando abbastanza rispetto a quanto vendo?",
            "Quali prodotti, servizi o attività mi lasciano più margine?",
            "I miei costi stanno crescendo più velocemente dei ricavi?",
            "Posso permettermi una nuova assunzione o un investimento?",
            "Avrò liquidità sufficiente per affrontare i prossimi pagamenti?"
          ]}
        />
        <Pull>Il controllo di gestione non serve a produrre altri numeri. Serve a prendere decisioni migliori con quelli che hai.</Pull>
        <P>E quando questi numeri vengono letti e discussi insieme a un commercialista che conosce la tua azienda, smettono di essere semplici dati e diventano indicazioni concrete su cosa fare.</P>
      </div>
    </section>

    {/* PICCOLE IMPRESE */}
    <section id="piccole-imprese" className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="controllo-gestione-sezione-piccole-imprese">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <H2 icon={Target}>Il controllo di gestione serve anche alle piccole imprese. Soprattutto alle piccole imprese.</H2>
        <P>C'è un'idea che sento ripetere spesso:</P>
        <div className="flex gap-4 bg-white border border-[#E8E2D5] rounded-lg p-6 my-6">
          <Quote className="text-gold flex-shrink-0" size={22} />
          <p className="text-[#0D1527] text-lg italic leading-relaxed">
            "Il controllo di gestione? Ma io ho una piccola azienda, mica una multinazionale!"
          </p>
        </div>
        <P>Eppure, pensaci.</P>
        <P>Una grande azienda può avere un direttore finanziario, un responsabile amministrativo e persone che analizzano i numeri ogni giorno.</P>
        <P>In una piccola impresa, invece, chi prende le decisioni?</P>
        <p className="font-heading text-5xl md:text-6xl text-gold my-6">Tu.</p>
        <P>Sei tu che decidi se assumere una persona, aumentare i prezzi, acquistare un macchinario, aprire un nuovo punto vendita o affrontare un investimento.</P>
        <P>E spesso devi prendere queste decisioni mentre gestisci clienti, fornitori, dipendenti e mille imprevisti.</P>
        <P>Senza avere davanti un quadro chiaro della situazione economica e finanziaria.</P>
        <P>Ma il fatto che la tua azienda sia piccola non rende meno importanti le tue decisioni. Anzi.</P>
        <P>Un aumento dei costi non previsto, un prezzo calcolato male o un investimento fatto nel momento sbagliato possono pesare moltissimo su un'impresa con risorse limitate.</P>
        <P>Ecco perché il controllo di gestione non è una questione di dimensioni.</P>
        <Pull>È una questione di consapevolezza.</Pull>
        <P>Non servono strutture complicate o report di cinquanta pagine.</P>
        <P>Servono pochi numeri, quelli giusti, aggiornati e spiegati in modo comprensibile.</P>
        <P>Per sapere se la tua attività sta producendo risultati, se può sostenere i prossimi impegni e dove puoi intervenire per migliorare.</P>
        <P>Non devi aspettare che la tua azienda diventi grande per iniziare a gestirla con metodo.</P>
        <P className="font-bold text-[#0D1527]">Devi darle gli strumenti per crescere senza perdere il controllo.</P>
      </div>
    </section>

    {/* NUMERI OGNI MESE */}
    <section id="numeri-ogni-mese" className="py-16 md:py-20 bg-[#FAF8F5]" data-testid="controllo-gestione-sezione-numeri">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <H2 icon={BarChart3}>I numeri che ogni imprenditore dovrebbe conoscere ogni mese</H2>
          <P>Non hai bisogno di conoscere a memoria tutte le voci del bilancio.</P>
          <P>Hai bisogno di sapere quali numeri stanno facendo crescere la tua azienda e quali, invece, rischiano di metterla in difficoltà.</P>
          <P>E soprattutto hai bisogno di conoscerli quando puoi ancora fare qualcosa per cambiarli.</P>
          <P className="font-bold text-[#0D1527]">Ecco alcune delle informazioni che possiamo analizzare insieme, mese dopo mese.</P>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {NUMERI.map((n, i) => (
            <article key={n.h3} className="bg-white border border-[#E8E2D5] rounded-lg p-7" data-testid={`controllo-gestione-numero-${i + 1}`}>
              <n.icon className="text-gold mb-4" size={26} />
              <H3>{n.h3}</H3>
              {n.p.map((t) => (
                <p key={t} className="text-[#4A5568] text-sm leading-relaxed mb-3 last:mb-0">{t}</p>
              ))}
            </article>
          ))}
        </div>

        <div className="max-w-3xl mt-14">
          <H3>Il punto non è avere più numeri. È avere più risposte.</H3>
          <P>Alla fine di ogni analisi dovresti poter rispondere a tre domande:</P>
          <div className="grid sm:grid-cols-3 gap-4 my-6" data-testid="controllo-gestione-tre-domande">
            {["Dove siamo oggi?", "Dove stiamo andando?", "Che cosa possiamo fare per migliorare?"].map((d) => (
              <div key={d} className="bg-[#0D1527] text-white rounded-lg p-5 font-heading text-lg leading-snug">{d}</div>
            ))}
          </div>
          <P className="font-bold text-[#0D1527]">È questo il valore del controllo di gestione: trasformare i dati della tua impresa in decisioni concrete.</P>
        </div>
      </div>
    </section>

    {/* COME LAVORIAMO */}
    <section id="come-lavoriamo" className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="controllo-gestione-sezione-come-lavoriamo">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <H2 icon={Compass}>Come lavoriamo insieme: dai numeri alle decisioni</H2>
        <P>Un software può mostrarti un grafico.</P>
        <P>Un bilancio può dirti se hai guadagnato o perso.</P>
        <P>Ma quando scopri che i tuoi margini stanno diminuendo, che il costo del personale è aumentato o che tra tre mesi potresti avere un problema di liquidità, la domanda più importante è un'altra:</P>
        <Pull>E adesso, cosa facciamo?</Pull>
        <P>È qui che cambia il modo di lavorare.</P>
        <P>Nel mio studio il controllo di gestione non è un documento che ricevi e devi interpretare da solo.</P>
        <P>È un percorso in cui analizziamo periodicamente i numeri della tua azienda, li traduciamo in informazioni comprensibili e li utilizziamo per valutare insieme le decisioni da prendere.</P>

        <div className="space-y-8 mt-10">
          {PASSI_LAVORO.map((s, i) => (
            <div key={s.h3} className="flex gap-5" data-testid={`controllo-gestione-passo-lavoro-${i + 1}`}>
              <span className="font-heading text-3xl text-gold/70 leading-none pt-1 w-9 flex-shrink-0">{i + 1}</span>
              <div>
                <H3>{s.h3}</H3>
                {s.p.map((t) => (
                  <p key={t} className="text-[#3B4658] leading-relaxed mb-3 last:mb-0">{t}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <P>Un confronto che non si limita alle scadenze fiscali.</P>
          <P>Un partner con cui ragionare sui numeri mentre costruisci i risultati della tua azienda.</P>
          <Pull>Perché il vero valore del controllo di gestione non è sapere quanti soldi hai guadagnato ieri. È capire cosa puoi fare oggi per migliorare i risultati di domani.</Pull>
        </div>
      </div>
    </section>

    {/* A CHI È ADATTO */}
    <section id="a-chi-e-adatto" className="py-16 md:py-20 bg-[#FAF8F5]" data-testid="controllo-gestione-sezione-a-chi-adatto">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <H2 icon={Target}>A chi è adatto il controllo di gestione?</H2>
        <P>Non devi avere un'azienda con milioni di euro di fatturato per aver bisogno del controllo di gestione.</P>
        <P>Devi avere un'attività da gestire, decisioni da prendere e la volontà di capire davvero come sta andando.</P>
        <P>Il mio servizio si rivolge soprattutto a titolari di micro e piccole imprese, in particolare SRL, che non vogliono limitarsi a conoscere il risultato dell'anno precedente.</P>

        <div className="mt-10">
          <H3>È il servizio giusto per te se…</H3>
          <div className="space-y-4 mt-5" data-testid="controllo-gestione-adatto-a">
            {ADATTO_A.map(([titolo, testo]) => (
              <div key={titolo} className="bg-white border border-[#E8E2D5] rounded-lg p-5">
                <p className="font-bold text-[#0D1527] mb-1">{titolo}</p>
                <p className="text-[#4A5568] text-sm leading-relaxed">{testo}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12">
          <H3>Lavoriamo con imprese di settori diversi</H3>
          <P>Un ristorante, un negozio, un centro estetico o un'impresa di servizi hanno dinamiche differenti.</P>
          <P>Ma condividono alcune domande fondamentali:</P>
          <Pull>Sto guadagnando abbastanza? Dove posso migliorare? Posso permettermi di crescere?</Pull>
          <P>Il controllo di gestione viene adattato alle caratteristiche dell'attività, perché gli indicatori davvero utili non sono necessariamente gli stessi per tutti.</P>
        </div>

        <div className="mt-12">
          <H3>Quando potrebbe non essere il servizio adatto a te</H3>
          <P>Se cerchi esclusivamente qualcuno che presenti le dichiarazioni fiscali o si occupi degli adempimenti obbligatori, probabilmente non hai bisogno di un percorso di controllo di gestione.</P>
          <P>E se desideri un report da ricevere e archiviare senza dedicare tempo a comprenderlo e utilizzarlo, rischieresti di non sfruttarne il valore.</P>
          <P>Questo servizio funziona quando c'è la volontà di guardare i numeri, confrontarsi e utilizzarli per prendere decisioni.</P>
          <P>Non devi essere esperto di economia aziendale.</P>
          <P>Devi soltanto voler conoscere meglio la tua impresa.</P>
          <P className="font-bold text-[#0D1527]">Perché non si può migliorare ciò che non si conosce. E non si può guidare un'azienda guardando soltanto nello specchietto retrovisore.</P>
        </div>
      </div>
    </section>

    {/* COME INIZIAMO */}
    <section id="come-iniziamo" className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="controllo-gestione-sezione-come-iniziamo">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <H2 icon={ArrowRight}>Come iniziamo a lavorare insieme?</H2>
        <P>Forse ti stai chiedendo quanto sia complicato introdurre il controllo di gestione nella tua azienda.</P>
        <P>La risposta è più semplice di quanto immagini.</P>
        <P className="font-bold text-[#0D1527]">Non devi cambiare il tuo modo di fare l'imprenditore. Devi iniziare ad avere le informazioni giuste per farlo meglio.</P>

        <div className="space-y-8 mt-10">
          {PASSI_AVVIO.map((s, i) => (
            <div key={s.h3} className="flex gap-5" data-testid={`controllo-gestione-passo-avvio-${i + 1}`}>
              <span className="font-heading text-3xl text-gold/70 leading-none pt-1 w-9 flex-shrink-0">{i + 1}</span>
              <div>
                <H3>{s.h3}</H3>
                {s.p.map((t) => (
                  <p key={t} className="text-[#3B4658] leading-relaxed mb-3 last:mb-0">{t}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <H3>Da dove cominciamo?</H3>
          <P>Da una conversazione sulla tua azienda.</P>
          <P>Non serve che tu abbia già tutto sotto controllo. Anzi, il motivo per cui potresti aver bisogno di questo servizio è proprio che oggi non riesci ad avere una visione sufficientemente chiara.</P>
          <P>Raccontami come lavori, quali difficoltà incontri e quali risultati vorresti raggiungere.</P>
          <P>Capiremo insieme se il controllo di gestione può essere lo strumento giusto per la tua impresa.</P>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section id="domande-frequenti" className="py-16 md:py-20 bg-[#FAF8F5]" data-testid="controllo-gestione-faq">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <H2 icon={HelpCircle}>Le domande più frequenti sul controllo di gestione</H2>
        <div className="space-y-5 mt-8">
          {FAQ.map((f, i) => (
            <article key={f.q} className="bg-white border border-[#E8E2D5] rounded-lg p-6" data-testid={`controllo-gestione-faq-${i + 1}`}>
              <H3>{f.q}</H3>
              {f.a.map((t) => (
                <p key={t} className="text-[#4A5568] text-sm leading-relaxed mb-2 last:mb-0">{t}</p>
              ))}
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* COLLEGAMENTI INTERNI */}
    <section className="py-14 bg-[#F4EFEA] border-y border-[#E8E2D5]" data-testid="controllo-gestione-link-interni">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="font-heading text-xl text-[#0D1527] mb-6">Continua ad approfondire</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { to: "/metodo", titolo: "Il metodo del controllo direzionale", nota: "Come leggiamo i numeri mentre sei in corsa" },
            { to: "/pannello-direzionale", titolo: "Il pannello di controllo direzionale", nota: "Il cruscotto con i tuoi indicatori aggiornati" },
            { to: "/servizi", titolo: "I livelli di servizio dello studio", nota: "Dalla contabilità alla consulenza direzionale" },
            { to: "/chi-siamo", titolo: "Chi siamo", nota: "Silvia Casali e il team dello studio" }
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="group flex items-start justify-between gap-4 bg-white border border-[#E8E2D5] hover:border-gold rounded-lg p-5 transition-colors"
              data-testid={`controllo-gestione-link-${l.to.replace(/\//g, '')}`}
            >
              <span>
                <span className="block font-bold text-[#0D1527] group-hover:text-gold transition-colors">{l.titolo}</span>
                <span className="block text-[#4A5568] text-sm mt-1">{l.nota}</span>
              </span>
              <ArrowRight className="text-gold flex-shrink-0 mt-1" size={18} />
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* CHIUSURA + CTA */}
    <section className="py-16 md:py-24 bg-[#0D1527]" data-testid="controllo-gestione-cta-finale">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-8">
          La tua azienda merita più di un bilancio una volta all'anno.
        </h2>
        {[
          "Hai investito tempo, energie e risorse nella tua impresa.",
          "Ogni giorno affronti problemi, prendi decisioni, ti assumi responsabilità.",
          "Non dovresti dover indovinare se tutto questo lavoro ti sta facendo guadagnare.",
          "Dovresti poter conoscere i tuoi numeri, capire dove stai andando e sapere quali aspetti della gestione richiedono attenzione.",
          "Senza dover diventare un esperto di economia aziendale.",
          "Senza aspettare mesi per scoprire che qualcosa non ha funzionato.",
          "E senza affrontare da solo ogni decisione."
        ].map((t) => (
          <p key={t} className="text-white/75 leading-relaxed mb-4">{t}</p>
        ))}

        <div className="border-l-4 border-gold pl-6 my-10">
          <p className="text-white font-bold mb-3">Sono Silvia Casali, Dottore Commercialista e Revisore Legale.</p>
          <p className="text-white/75 leading-relaxed mb-3">
            Con il mio studio affianco gli imprenditori nella gestione economica, finanziaria e fiscale delle loro attività,
            attraverso contabilità aggiornata, analisi periodiche, previsioni e consulenza direzionale.
          </p>
          <p className="text-white/75 leading-relaxed">
            Il mio lavoro non è soltanto dirti quanto hai guadagnato. È aiutarti a capire come puoi migliorare i risultati della tua azienda.
          </p>
        </div>

        <h3 className="font-heading text-2xl text-white mb-4">Cominciamo dai tuoi numeri.</h3>
        <p className="text-white/75 leading-relaxed mb-3">
          Raccontami la tua attività, le difficoltà che incontri e le decisioni che vorresti affrontare con maggiore sicurezza.
        </p>
        <p className="text-white/75 leading-relaxed mb-10">
          Valuteremo insieme se e come il controllo di gestione può aiutarti.
        </p>

        <Link
          to="/contatti"
          className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105"
          data-testid="controllo-gestione-cta-contatti"
        >
          PARLIAMO DELLA TUA AZIENDA <ArrowRight size={18} />
        </Link>

        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-white/90 font-bold">Silvia Casali — Il commercialista come partner strategico.</p>
          <p className="text-white/55 text-sm mt-1">Studio a Ravenna. Consulenza anche online per imprese in tutta Italia.</p>
        </div>
      </div>
    </section>
  </main>
);

export { ControlloGestionePage };
